import Enumerable from "linq";
import store from "../../../store";
import { ReceptionSearchActiveKinds, ReceptionDetailStatusSearchCondition, WorkflowStatusSearchCondition } from ".";
import { WorkflowStatusName, ReceptionReportType, ReceptionDeliveryKind, ReceptionInputSection } from "../items";
import { InOutKinds, InOutKindsName, ReceptionDateKinds, ReceptionDateKindsName, WorkflowStatusKinds } from "../kinds";

import { Patient } from "../../patient";
import { DateDuration } from "../../system";
import { FormatReference } from "../../format";
import { User } from "../../user";
import { Department } from "../../department";
import { ReceptionDateTranslator } from "../ReceptionDateTranslator";
import { IConfiguration } from "../../../domain/configuration";

// 依頼詳細検索条件
export class ReceptionDetailSearchCondition {

  dateKind: ReceptionDateKinds;
  duration: DateDuration;
  durationPickerKey: string;
  limitDate: DateDuration;
  limitDatePickerKey: string;

  patient: Patient;
  paperStatus: ReceptionSearchActiveKinds;
  reportType: ReceptionReportType;
  formats: FormatReference[];
  paperName: string;

  managers: User[];
  isManagerMyself: boolean;
  creators: User[];
  isCreatorMyself: boolean;
  isDepartmentGroupSpecified: boolean;
  departments: Department[];
  receptionNumber: string;
  // 他医依頼有無
  deliveryType: ReceptionDeliveryKind;
  generalFlag1Status: ReceptionSearchActiveKinds;
  statuses: ReceptionDetailStatusSearchCondition;
  workflowStatuses: WorkflowStatusSearchCondition[];
  inputSection: ReceptionInputSection;
  inOutType: InOutKinds;

  // 患者、診療科を指定（プリセットのみ）
  isPatientIdSpecified: boolean;
  isDepartmentIdSpecified: boolean;
  presetPatient: Patient;
  presetDepartments: Department[];

  private _dateTranslator: ReceptionDateTranslator;

  constructor() {
    this.duration = new DateDuration(null, null);
    this.limitDate = new DateDuration(null, null);
    this.paperStatus = ReceptionSearchActiveKinds.NotSpecificated;
    this.reportType = null;
    this.formats = [];
    this.managers = [];
    this.isManagerMyself = false;
    this.creators = [];
    this.isCreatorMyself = false;
    this.isDepartmentGroupSpecified = false;
    this.departments = [];
    this.generalFlag1Status = ReceptionSearchActiveKinds.NotSpecificated;
    this.statuses = new ReceptionDetailStatusSearchCondition();
    this.workflowStatuses = [];
    this._dateTranslator = new ReceptionDateTranslator();
    this.deliveryType = new ReceptionDeliveryKind();
    this.receptionNumber = "";
    this.inputSection = null;
    this.inOutType = InOutKinds.None;
    this.isPatientIdSpecified = false;
    this.isDepartmentIdSpecified = false;
    this.presetPatient = null;
    this.presetDepartments = [];
  }

  setWorkflowStatuses(wfStatusList: WorkflowStatusName[], isAllSelect: boolean = false): void {
    this.workflowStatuses = [];
    wfStatusList.forEach((status, i) => {
      const wfStatus = new WorkflowStatusSearchCondition();
      wfStatus.isSelected = (isAllSelect || status.type === WorkflowStatusKinds.Avtive) ? true : false;
      wfStatus.status = status;
      this.workflowStatuses.push(wfStatus);
    });
  }

  setWorkflowStatusesSelectedState(value: boolean): void {
    this.workflowStatuses.forEach(m => {
      m.isSelected = value;
    });
  }

  setWorkflowStatusSelectedState(status: WorkflowStatusName, value: boolean): void {
    const updateStatus = Enumerable.from(this.workflowStatuses).firstOrDefault(s => s.status.code === status.code);
    if (!!updateStatus) {
      updateStatus.isSelected = value;
    }
  }

  // 検索実行確認が必要か？
  needConfirm(finishStatus: WorkflowStatusName): boolean {
    // 期間指定が無い＆終了が選択されている＆それ以外の条件がない場合は確認が必要

    if (this.duration.from && this.duration.to && this.duration.years <= 5) {
      // 期間指定があり、期間が5年以内
      return false;
    }
    if (!Enumerable.from(this.workflowStatuses).any(m => m.status.code === finishStatus.code && m.isSelected)) {
      // 終了進捗が選択されていない
      return false;
    }
    if (this.statuses.isBacked || this.statuses.isExpired || this.statuses.isTemporary) {
      // ステータスが何か選択されている
      return false;
    }
    if (this.patient) {
      // 患者が選択されている
      return false;
    }
    // あまり絞り込めない条件なので対象外
    // if (this.reportType) {
    //   // 文書種別が選択されている
    //   return false;
    // }
    if (this.formats.length > 0) {
      // フォーマットが選択されている
      return false;
    }
    if (this.managers.length > 0 || this.isManagerMyself) {
      // 対応責任者が選択されている
      return false;
    }
    if (this.creators.length > 0 || this.isCreatorMyself) {
      // 依頼作成者が選択されている
      return false;
    }
    if (this.departments.length > 0) {
      // 診療科が選択されている
      return false;
    }
    if (this.limitDate.from && this.limitDate.to && this.limitDate.years <= 5) {
      // 締切日の期間指定があり、期間が5年以内
      return false;
    }
    if (this.receptionNumber) {
      // 受付番号が入力されている
      return false;
    }
    // あまり絞り込めない条件なので対象外
    // if (this.generalFlag1) {
    //   // 公開フラグが立っているもののみ
    //   return false;
    // }

    return true;
  }

  getSummary(generalFlag1Name: string): string {
    const patient = this.isPatientIdSpecified
      ? this.presetPatient
      : this.patient;
    const departments = this.isDepartmentIdSpecified
      ? this.presetDepartments
      : this.departments;
    const config = store.getters.configuration as IConfiguration;
    const summary = this.toDurationString(this.dateKind, this.duration) + 
      this.toDurationString(ReceptionDateKinds.LimitDate, this.limitDate) +
      (patient ? this.toItemString("患者番号", patient.id) : "") +
      (this.formats.length > 0 ? this.toItemString(config.wording.format.attributes.name + "名", "指定あり") : "") +
      (this.paperName ? this.toItemString("手書き" + config.wording.format.attributes.name + "名", this.paperName) : "") +
      (this.managers.length > 0 || this.isManagerMyself ? this.toItemString("対応責任者", "指定あり") : "") +
      (this.isDepartmentGroupSpecified ? this.toItemString(config.wording.common.departmentGroup, "指定あり") : "") +
      (departments.length > 0 ? this.toItemString(config.wording.common.department, "指定あり") : "") +
      (this.receptionNumber ? this.toItemString("受付番号", this.receptionNumber) : "") +
      this.toItemString("ステータス", this.statuses.getSummary()) +
      this.toWorkflowStatusString();
     
    const subSummary =
      (this.paperStatus !== ReceptionSearchActiveKinds.NotSpecificated
        ? "手書き" + (this.paperStatus === ReceptionSearchActiveKinds.Inactivated ? "なし " : " ")
        : "") + 
      (this.reportType ? this.toItemString(config.wording.format.attributes.name + "種別", this.reportType.name) : "") + 
      (this.creators.length > 0 || this.isCreatorMyself ? this.toItemString("依頼作成者", "指定あり") : "") +
      (this.deliveryType.code !== 0 ? this.toItemString("交付方法", this.deliveryType.name) : "") +
      (this.inputSection ? this.toItemString("入力部署", this.inputSection.name) : "") +
      // // 他医依頼有無
      (this.generalFlag1Status !== ReceptionSearchActiveKinds.NotSpecificated
        ? generalFlag1Name + (this.generalFlag1Status === ReceptionSearchActiveKinds.Activated ? " " : "なし ")
        : "") +
      (this.inOutType !== InOutKinds.None ? this.toItemString("入院/外来", InOutKindsName.getDisplayName(this.inOutType)) : "");

    if (summary.length === 0) {
      // 主要な検索条件が指定されていなければ補助的な条件を表示
      return subSummary.length > 0 ? subSummary : "";
    } else {
      return subSummary.length > 0 ? (summary + " 他") : summary;
    }
  }

  private toDurationString(kind: ReceptionDateKinds, value: DateDuration): string {
    if (value && (value.from || value.to)) {
      return ReceptionDateKindsName.getDisplayName(kind) + "：" + this._dateTranslator.toDateDispString(value.from) + "～" + this._dateTranslator.toDateDispString(value.to) + " ";
    } 
    return "";
  }

  private toWorkflowStatusString(): string {

    let value = "";
    this.workflowStatuses.forEach((status, i) => {
      let summary = status.getSummary();
      if (summary) { summary += ","; }
      value += summary;
    });

    if (value.length === 0) { value = "進行中"; }

    return this.toItemString("進捗", value);
  }

  private toItemString(itemLabel: string, value: string): string {
    if (value.length === 0) { return ""; }
    return itemLabel + "：" + value + " ";
  }

  /**
   * false: 手書き以外の依頼だけに絞り込み
   * true: 絞り込みの条件にしない
   */
  get isPaperIncluded(): boolean {
    return this.paperStatus === ReceptionSearchActiveKinds.NotSpecificated;
  }

  set isPaperIncluded(value: boolean) {
    this.paperStatus = value
      ? ReceptionSearchActiveKinds.NotSpecificated
      : ReceptionSearchActiveKinds.Inactivated;
  }

  clone(): ReceptionDetailSearchCondition {
    const newObj = new ReceptionDetailSearchCondition();
    newObj.dateKind = this.dateKind;
    newObj.duration = this.duration;
    newObj.durationPickerKey = this.durationPickerKey;
    newObj.limitDate = this.limitDate;
    newObj.limitDatePickerKey = this.limitDatePickerKey;
    newObj.patient = this.patient;
    newObj.paperStatus = this.paperStatus;
    newObj.reportType = this.reportType;
    newObj.formats = this.formats;
    newObj.paperName = this.paperName;
    newObj.managers = this.managers;
    newObj.isManagerMyself = this.isManagerMyself;
    newObj.creators = this.creators;
    newObj.isCreatorMyself = this.isCreatorMyself;
    newObj.isDepartmentGroupSpecified = this.isDepartmentGroupSpecified;
    newObj.departments = this.departments;
    newObj.receptionNumber = this.receptionNumber;
    // 他医依頼有無
    newObj.deliveryType = this.deliveryType;
    newObj.generalFlag1Status = this.generalFlag1Status;
    newObj.statuses = this.statuses.clone();
    newObj.inputSection = this.inputSection;
    newObj.inOutType = this.inOutType;

    newObj.workflowStatuses = [];
    this.workflowStatuses.forEach((status, i) => {
      const wfStatus = status.clone();
      newObj.workflowStatuses.push(wfStatus);
    });

    // プリセット患者、診療科
    newObj.isPatientIdSpecified = this.isPatientIdSpecified;
    newObj.isDepartmentIdSpecified = this.isDepartmentIdSpecified;
    newObj.presetPatient = this.presetPatient;
    newObj.presetDepartments = this.presetDepartments;
    return newObj;

  }
}
